import React, { useState, useMemo, useCallback, useRef } from "react";
import { useData, USER_TABLE } from "../context/DataContext";
import { cellText, getStatusBadge, getPriorityBadge, formatDate, formatDateTime, normalizeToMs } from "../utils/helpers";
import EditTaskModal from "./EditTaskModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import CommentPanel from "./CommentPanel";
import CustomSelect from "./CustomSelect";
import { useAuth } from "../context/AuthContext";

const KEY_COLS = [
  "Task ID", "Task Name", "Overall Status", "Priority",
  "Assigned Users", "Assigned By", "Supervisor",
  "Start Date", "End Date", "Company Name",
  "Assignee Department", "Creator Department",
  "Recurring Type", "Task Description",
];

export default function TaskTable({
  records,
  tableId,
  emptyMsg = "No tasks found",
  canEdit = false,
  canDelete = false,
  canComplete = false,
  statusOptionsOverride = null,
  hideStatusFilter = false,
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priFilter, setPriFilter] = useState("all");
  const [view, setView] = useState("kanban"); // "kanban" | "grid" | "gantt"
  const statusOptions = useMemo(() => {
    if (statusOptionsOverride) return statusOptionsOverride;
    const opts = new Set();
    (records || []).forEach(r => {
      const s = cellText(r.fields?.["Overall Status"]) || "Not Started";
      opts.add(s);
    });
    return Array.from(opts).sort((a, b) => {
      // Keep "Done" related items at the bottom of the list for better UX
      const ad = a.toLowerCase().includes("done");
      const bd = b.toLowerCase().includes("done");
      if (ad && !bd) return 1;
      if (!ad && bd) return -1;
      return a.localeCompare(b);
    });
  }, [records, statusOptionsOverride]);
  const [editRecord, setEditRecord] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [completing, setCompleting] = useState({});
  const [expanded, setExpanded] = useState({});
  const [commentTask, setCommentTask] = useState(null);
  const [taskAttachments, setTaskAttachments] = useState({});
  const loadedRef = useRef({});

  const { user } = useAuth();
  const { updateTask, deleteTask, refresh, getAttachments, deleteAttachment, uploadFile, larkUsers } = useData();
  const isAdmin = user?.role === "admin";

  const loadAttachments = useCallback(async (taskId) => {
    if (!taskId || loadedRef.current[taskId]) return;
    loadedRef.current[taskId] = true;
    setTaskAttachments(p => ({ ...p, [taskId]: [] }));
    try {
      const data = await getAttachments(taskId);
      setTaskAttachments(p => ({ ...p, [taskId]: data }));
    } catch (e) { console.error(e); }
  }, [getAttachments]);

  const handleAttachFile = async (taskId, file) => {
    if (!file || !taskId) return;
    try {
      await uploadFile(file, String(taskId));
      const data = await getAttachments(taskId);
      setTaskAttachments(p => ({ ...p, [taskId]: data }));
    } catch (e) {
      alert("Attachment failed: " + e.message);
    }
  };
  const showActions = canEdit || canDelete || canComplete;

  const handleComplete = useCallback(async (row) => {
    const rid = row.record_id;
    const newVal = !row.fields?.["My Completion"];
    setCompleting(p => ({ ...p, [rid]: true }));
    try {
      // Only send "My Completion" — worker will set "Completed By" on ADMIN_TABLE using JWT
      const updateFields = { "My Completion": newVal };
      await updateTask(tableId, rid, updateFields);
    } catch (e) {
      alert("Failed: " + e.message);
    } finally {
      setCompleting(p => ({ ...p, [rid]: false }));
    }
  }, [updateTask, tableId, user?.userId]);

  const filtered = useMemo(() => {
    if (!records) return [];
    let rows = records;
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(r => KEY_COLS.some(k => cellText((r.fields || {})[k]).toLowerCase().includes(q)));
    }
    if (statusFilter !== "all") {
      rows = rows.filter(r => {
        const s = cellText((r.fields || {})["Overall Status"]) || "Not Started";
        return s === statusFilter;
      });
    }
    if (priFilter !== "all") {
      rows = rows.filter(r => cellText((r.fields || {})["Priority"]).toLowerCase().includes(priFilter));
    }
    return [...rows].sort((a, b) => {
      const av = (a.fields || {})["End Date"];
      const bv = (b.fields || {})["End Date"];
      const aVal = typeof av === "number" ? av : (typeof av === "object" && av?.value ? av.value : Infinity);
      const bVal = typeof bv === "number" ? bv : (typeof bv === "object" && bv?.value ? bv.value : Infinity);
      return aVal - bVal;
    });
  }, [records, search, statusFilter, priFilter]);

  // ── Gantt date range ──────────────────────────────────────
  const ganttRange = useMemo(() => {
    if (filtered.length === 0) {
      const now = Date.now();
      return { start: now - 7 * 86400000, end: now + 23 * 86400000, totalMs: 30 * 86400000 };
    }
    let minMs = Infinity, maxMs = -Infinity;
    filtered.forEach(r => {
      const s = normalizeToMs((r.fields || {})["Start Date"]);
      const e = normalizeToMs((r.fields || {})["End Date"]);
      if (s > 0 && s < minMs) minMs = s;
      if (e > 0 && e > maxMs) maxMs = e;
    });
    if (minMs === Infinity) minMs = Date.now();
    if (maxMs === -Infinity) maxMs = minMs + 30 * 86400000;
    const pad = 7 * 86400000;
    const start = minMs - pad;
    const end = maxMs + pad;
    return { start, end, totalMs: end - start };
  }, [filtered]);

  const sc = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "overdue done") return { bg: "#FFF7ED", color: "#C2410C", border: "1px solid #FFEDD5" };
    if (s.includes("overdue")) return { bg: "#FEE2E2", color: "#991B1B" };
    if (s.includes("done") || s.includes("complet")) return { bg: "#D1FAE5", color: "#065F46" };
    if (s.includes("progress")) return { bg: "#FEF3C7", color: "#92400E" };
    return { bg: "#F1F5F9", color: "#475569" };
  };

  return (
    <div>
      <style>
        {`
          @keyframes gaming-stripes {
            0% { background-position: 0 0; }
            100% { background-position: 28px 0; }
          }
        `}
      </style>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 180 }}>
          <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--gray-400)", pointerEvents: "none" }}>🔍</span>
          <input
            style={{ width: "100%", padding: "9px 12px 9px 34px", border: "1.5px solid var(--gray-200)", borderRadius: 10, fontSize: 13, outline: "none", background: "white", color: "var(--gray-800)", fontFamily: "inherit" }}
            placeholder="Search tasks..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {!hideStatusFilter && (
          <CustomSelect 
            placeholder="All Status"
            options={statusOptions}
            value={statusFilter === "all" ? "" : statusFilter}
            onChange={val => setStatusFilter(val || "all")}
            isClearable={true}
            style={{ minWidth: 160 }}
          />
        )}
        <CustomSelect 
          placeholder="All Priority"
          options={["Low", "Medium", "High"]}
          value={priFilter === "all" ? "" : priFilter}
          onChange={val => setPriFilter(val || "all")}
          isClearable={true}
          style={{ minWidth: 160 }}
        />
        <div style={{ padding: "9px 14px", background: "white", border: "1.5px solid var(--gray-200)", borderRadius: 10, fontSize: 12, color: "var(--gray-500)", display: "flex", alignItems: "center", whiteSpace: "nowrap", fontWeight: 600 }}>
          {filtered.length} / {records?.length || 0}
        </div>
        {/* View switcher */}
        <div style={{ display: "flex", border: "1.5px solid var(--gray-200)", borderRadius: 10, overflow: "hidden", flexShrink: 0 }}>
          {[
            { key: "kanban", icon: "⊡", label: "Kanban" },
            { key: "grid",   icon: "☰", label: "Grid" },
            { key: "gantt",  icon: "📊", label: "Gantt" },
          ].map((v, idx) => (
            <button key={v.key} onClick={() => setView(v.key)} style={{
              padding: "9px 14px", border: "none", borderLeft: idx > 0 ? "1.5px solid var(--gray-200)" : "none",
              fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              background: view === v.key ? "var(--primary)" : "white",
              color: view === v.key ? "white" : "var(--gray-500)",
              transition: "all 0.15s ease", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
            }}>{v.icon} {v.label}</button>
          ))}
        </div>
      </div>

      {/* Empty */}
      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--gray-400)" }}>
          <div style={{ fontSize: 48, marginBottom: 14, opacity: 0.5 }}>📭</div>
          <p style={{ fontSize: 14, fontWeight: 600 }}>{search ? "No tasks match your search." : emptyMsg}</p>
        </div>
      )}

      {/* ── KANBAN VIEW ─────────────────────────────────── */}
      {view === "kanban" && <div style={{ display: "flex", gap: 20, alignItems: "flex-start", position: "relative" }}>

        {/* Cards Grid */}
        <div style={{ flex: 1, minWidth: 0, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
          {filtered.map((row, i) => {
            const f = row.fields || {};
            const rid = row.record_id || i;
            const statusText = cellText(f["Overall Status"]) || "Not Started";
            const isDone = statusText.toLowerCase().includes("done") || statusText.includes("✅");
            const isUserDone = !!f["My Completion"];
            const priority = cellText(f["Priority"]) || "Medium";
            const isOverdue = statusText.toLowerCase().includes("overdue");
            const priL = priority.toLowerCase();
            const isHigh = priL === "high" || priL === "important";
            const isMed = priL === "medium" || priL === "normal";
            const s = sc(statusText);
            const taskId = cellText(f["Task ID"]);
            const taskName = cellText(f["Task Name"]);
            const cardAttachments = taskId ? (taskAttachments[taskId] || []) : [];
            // REMOVED auto-load from render to avoid API storm
            // if (taskId && taskAttachments[taskId] === undefined) {
            //   setTimeout(() => loadAttachments(taskId), 0);
            // }
            const desc = cellText(f["Task Description"]);
            // Check all possible field names: "Total Assigned Users" (USER_TABLE), "Assigned Users" (ADMIN_TABLE), "User" (single user fallback)
            const assignedRaw = f["Total Assigned Users"] || f["All Assigned Users"] || f["Assigned Users"] || f["User"];
            let assignedNames = [];
            if (Array.isArray(assignedRaw)) {
              assignedNames = assignedRaw.map(v => {
                if (v.en_name || v.name) return v.en_name || v.name;
                if (v.id) {
                  const match = (larkUsers || []).find(u => u.id === v.id);
                  if (match) return match.name;
                }
                return null;
              }).filter(Boolean);
            }
            if (assignedNames.length === 0) {
              const rawStr = cellText(assignedRaw);
              if (rawStr && rawStr !== "—") assignedNames = rawStr.split(",").map(s => s.trim()).filter(Boolean);
            }
            const assignedUsersArr = assignedNames;


            const supervisor = cellText(f["Supervisor"]);
            const createdBy = cellText(f["Task Creator"]) !== "—" ? cellText(f["Task Creator"]) : "";
            const startDate = formatDate(f["Start Date"]);
            const endDate = formatDate(f["End Date"]);
            const company = cellText(f["Company Name"]);
            const dept = cellText(f["Assignee Department"]);
            const creatorDept = cellText(f["Creator Department"]);
            const completedAtRaw = f["Completed At"] || f["Completion Time"];
            const completedAt = completedAtRaw ? formatDateTime(completedAtRaw) : "";

            // Resolve "Completed By" directly from the Lark fields
            // "Task Completed By" is primarily used in USER_TABLE, "Completed Count Copy" in ADMIN_TABLE
            const rawCompletedBy = f["Task Completed By"] || f["Completed Count Copy"] || f["Completed By"];
            let completedBy = cellText(rawCompletedBy);

            if (Array.isArray(rawCompletedBy)) {
              const resolvedNames = rawCompletedBy.map(v => {
                if (v?.id) {
                  const match = (larkUsers || []).find(u => u.id === v.id);
                  if (match) return match.name;
                }
                return null;
              }).filter(Boolean);

              if (resolvedNames.length > 0 && completedBy === "") {
                completedBy = resolvedNames.join(", ");
              }
            }

            let completedCount = 0;
            if (completedBy && completedBy !== "—") {
              completedCount = completedBy.split(",").length;
            }
            const totalAssignedCount = assignedUsersArr.length || 1;
            const progressPercent = Math.min(100, Math.round((completedCount / totalAssignedCount) * 100));

          const normalizeToMs = (val) => {
            if (!val) return 0;
            let ms = 0;
            if (typeof val === "number") ms = val;
            else if (typeof val === "object" && val?.value) ms = val.value;
            else if (Array.isArray(val) && val[0]?.value) ms = val[0].value;
            else {
              const d = new Date(val);
              ms = isNaN(d.getTime()) ? 0 : d.getTime();
            }
            if (ms <= 0) return 0;
            // Handle Excel serial numbers (Lark specific)
            if (ms < 1000000) { // Using 1M to be safe (Excel serials are around 45k, JS timestamps are billions)
               ms = (ms - 25569) * 86400 * 1000;
            }
            return ms;
          };

          const completedTime = normalizeToMs(completedAtRaw);
          const dueTime = normalizeToMs(f["End Date"]);
          
          // Use individual completion for employee view, overall for admin view
          const effectivelyDone = tableId === USER_TABLE ? isUserDone : (statusText.toLowerCase().includes("done") || statusText.includes("✅"));
          const isLate = effectivelyDone && completedTime && dueTime && (completedTime > (dueTime + 60000)); 

            const recurring = cellText(f["Recurring Type"]);

            return (
              <div key={rid} style={{
                background: isLate ? "#FFF5F5" : "white", borderRadius: 14,
                border: `1px solid ${isOverdue || isLate ? "#FECACA" : isDone ? "#A7F3D0" : "var(--gray-200)"}`,
                boxShadow: isLate ? "0 2px 12px rgba(239, 68, 68, 0.08)" : "0 2px 8px rgba(0,0,0,0.05)",
                overflow: "hidden", transition: "transform 0.2s, box-shadow 0.2s",
                opacity: (tableId === USER_TABLE ? isUserDone : isDone) ? 0.82 : 1,
                display: "flex", flexDirection: "column",
              }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.1)";
                  if (taskId) loadAttachments(taskId); // Load on demand
                }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.05)"; }}
              >
                {/* Color bar */}
                <div style={{ height: 4, background: (isOverdue || isLate) ? "#EF4444" : isDone ? "#10B981" : isHigh ? "#F59E0B" : isMed ? "var(--primary)" : "#CBD5E1" }} />


                <div style={{ padding: "15px 15px 12px", display: "flex", flexDirection: "column", flex: 1 }}>
                  {/* Header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {taskId && (
                        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--primary)", marginBottom: 4, letterSpacing: "0.04em" }}>
                          {taskId}
                        </div>
                      )}
                      <div style={{
                        fontSize: 15, fontWeight: 800, color: (tableId === USER_TABLE ? isUserDone : isDone) ? "var(--gray-400)" : "var(--gray-900)",
                        lineHeight: 1.35, textDecoration: (tableId === USER_TABLE ? isUserDone : isDone) ? "line-through" : "none",
                      }}>
                        {taskName || "Untitled Task"}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5, flexShrink: 0 }}>
                      <span style={{ 
                        padding: "3px 9px", borderRadius: 20, fontSize: 10, fontWeight: 700, 
                        background: s.bg, 
                        color: s.color, 
                        border: s.border || "none",
                        whiteSpace: "nowrap" 
                      }}>
                        {statusText}
                      </span>
                      {(() => {
                        const pb = getPriorityBadge(priority);
                        return (
                          <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600, background: pb.bg, color: pb.color, whiteSpace: "nowrap", textAlign: "center" }}>
                            {pb.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Description */}
                  {desc && (
                    <div style={{ fontSize: 12, color: "var(--gray-500)", marginBottom: 12, lineHeight: 1.55, padding: "8px 10px", background: "var(--gray-50)", borderRadius: 8, borderLeft: "2px solid var(--gray-200)" }}>
                      {desc}
                    </div>
                  )}

                  {/* Dates */}
                  <div style={{ display: "flex", gap: 7, marginBottom: 10, flexWrap: "wrap" }}>
                    {startDate && startDate !== "—" && (
                      <span style={{ fontSize: 11, color: "var(--gray-500)", background: "var(--gray-50)", padding: "4px 9px", borderRadius: 6, display: "flex", alignItems: "center", gap: 4 }}>
                        📅 {startDate}
                      </span>
                    )}
                    {endDate && endDate !== "—" && (
                      <span style={{ fontSize: 11, fontWeight: isOverdue ? 700 : 400, color: isOverdue ? "#991B1B" : "var(--gray-500)", background: isOverdue ? "#FEE2E2" : "var(--gray-50)", padding: "4px 9px", borderRadius: 6, display: "flex", alignItems: "center", gap: 4 }}>
                        {isOverdue ? "🔴" : "⏰"} Due {endDate}
                      </span>
                    )}
                    {recurring && recurring !== "—" && recurring !== "Not Applicable" && (
                      <span style={{ fontSize: 11, color: "var(--purple)", background: "var(--purple-light)", padding: "4px 9px", borderRadius: 6, display: "flex", alignItems: "center", gap: 4 }}>
                        🔄 {recurring}
                      </span>
                    )}
                  </div>

                  {/* People */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7, marginBottom: 10 }}>
                    {assignedUsersArr.length > 0 && (
                      <div style={{ background: "#EEF2FF", borderRadius: 8, padding: "7px 10px", gridColumn: "1/-1" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--primary)", marginBottom: 4 }}>Assigned To</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {assignedUsersArr.map((u, idx) => (
                            <span key={idx} style={{ fontSize: 12, fontWeight: 600, color: "var(--gray-800)", background: "white", padding: "2px 8px", borderRadius: 12, border: "1px solid #C7D2FE", whiteSpace: "normal", wordBreak: "break-word" }}>{u}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {supervisor && supervisor !== "—" && (
                      <div style={{ background: "#F0FDF4", borderRadius: 8, padding: "7px 10px" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--success)", marginBottom: 2 }}>Supervisor</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--gray-800)", wordBreak: "break-word" }}>{supervisor}</div>
                      </div>
                    )}
                    {createdBy && createdBy !== "—" && (
                      <div style={{ background: "var(--gray-50)", borderRadius: 8, padding: "7px 10px" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--gray-400)", marginBottom: 2 }}>Task Creator</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--gray-700)", wordBreak: "break-word" }}>{createdBy}</div>
                      </div>
                    )}
                    {company && company !== "—" && (
                      <div style={{ background: "var(--gray-50)", borderRadius: 8, padding: "7px 10px" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--gray-400)", marginBottom: 2 }}>Company</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--gray-700)", wordBreak: "break-word" }}>{company}</div>
                      </div>
                    )}
                    {dept && dept !== "—" && (
                      <div style={{ background: "var(--gray-50)", borderRadius: 8, padding: "7px 10px" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--gray-400)", marginBottom: 2 }}>Assignee Dept</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--gray-700)", wordBreak: "break-word" }}>{dept}</div>
                      </div>
                    )}
                    {creatorDept && creatorDept !== "—" && (
                      <div style={{ background: "var(--gray-50)", borderRadius: 8, padding: "7px 10px" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--gray-400)", marginBottom: 2 }}>Creator Dept</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--gray-700)", wordBreak: "break-word" }}>{creatorDept}</div>
                      </div>
                    )}
                    {completedAt && completedAt !== "—" && isDone && (
                      <div style={{ background: "#D1FAE5", borderRadius: 8, padding: "7px 10px" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--success)", marginBottom: 2 }}>Completed At</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--gray-800)" }}>{completedAt}</div>
                      </div>
                    )}
                    {completedBy && completedBy !== "—" && (
                      <div style={{ background: isDone ? "#ECFDF5" : "#FFF7ED", borderRadius: 8, padding: "7px 10px", gridColumn: "1/-1" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: isDone ? "#059669" : "#D97706", marginBottom: 4 }}>
                          {isDone ? "✅ Completed By" : "⏳ Completed By (Partial)"}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {completedBy.split(",").map((name, idx) => (
                            <span key={idx} style={{ fontSize: 11, fontWeight: 600, color: isDone ? "#065F46" : "#92400E", background: isDone ? "#D1FAE5" : "#FEF3C7", padding: "2px 8px", borderRadius: 10, border: `1px solid ${isDone ? "#6EE7B7" : "#FCD34D"}`, whiteSpace: "normal", wordBreak: "break-word" }}>
                              {name.trim()}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>



                  {/* Attachments */}
                  {cardAttachments.length > 0 && (
                    <div style={{ marginTop: 12, padding: "10px 12px", background: "#FAFAFA", borderRadius: 10, border: "1px solid var(--gray-200)" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--gray-500)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
                        <span>📎 Attachments</span>
                        <span style={{ fontSize: 9, fontWeight: 400, color: "var(--gray-400)" }}>({cardAttachments.length})</span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {cardAttachments.map((a, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "4px 0", borderBottom: i === cardAttachments.length - 1 ? "none" : "1px solid var(--gray-100)" }}>
                            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                              <a href={`https://task-system.aquint-automation.workers.dev/api/download/${a.fileToken}`}
                                target="_blank" rel="noopener noreferrer"
                                style={{
                                  fontSize: 12, fontWeight: 600, color: "var(--primary)",
                                  textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                  display: "block"
                                }}>
                                📄 {a.fileName}
                              </a>
                              <div style={{ fontSize: 9, color: "var(--gray-400)", marginTop: 1 }}>
                                By {a.uploaderName || "Unknown"}
                              </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <a href={`https://task-system.aquint-automation.workers.dev/api/download/${a.fileToken}`}
                                target="_blank" rel="noopener noreferrer"
                                className="btn-icon" style={{ padding: 4, fontSize: 12 }} title="Download">
                                📥
                              </a>
                              {isAdmin && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Delete attachment "${a.fileName}"?`)) {
                                      try {
                                        const idForDelete = taskId && taskId !== "—" ? taskId : rid;
                                        await deleteAttachment(idForDelete, a.fileToken);
                                        // Refresh only this card's attachments
                                        loadAttachments(idForDelete);
                                      } catch (err) { alert(err.message); }
                                    }
                                  }}
                                  className="btn-icon" style={{ padding: 4, fontSize: 12, color: "var(--danger)" }} title="Delete Attachment"
                                >
                                  🗑️
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions & Progress */}
                  {showActions && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 12, borderTop: "1px solid var(--gray-100)", marginTop: "auto" }}>
                      
                      {/* Gaming Style Progress Bar */}
                      {assignedUsersArr.length > 0 && (
                        <div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--gray-500)" }}>Task Progress</span>
                            <span style={{ fontSize: 11, fontWeight: 800, color: progressPercent === 100 ? "#10B981" : "var(--primary)" }}>
                              {progressPercent}% <span style={{ fontSize: 9, color: "var(--gray-400)" }}>({completedCount}/{totalAssignedCount})</span>
                            </span>
                          </div>
                          <div style={{ width: "100%", height: 8, background: "#E2E8F0", borderRadius: 10, overflow: "hidden", boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)", position: "relative" }}>
                            <div style={{
                              width: `${progressPercent}%`, height: "100%",
                              background: progressPercent === 100 
                                ? "linear-gradient(90deg, #10B981, #059669)" 
                                : "repeating-linear-gradient(45deg, #3B82F6, #3B82F6 10px, #2563EB 10px, #2563EB 20px)",
                              backgroundSize: "28px 28px",
                              animation: progressPercent < 100 ? "gaming-stripes 1s linear infinite" : "none",
                              borderRadius: 10,
                              transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                              boxShadow: progressPercent === 100 ? "0 0 10px rgba(16,185,129,0.5)" : "0 0 10px rgba(59,130,246,0.5)",
                            }} />
                          </div>
                        </div>
                      )}

                      <div style={{ display: "flex", gap: 8 }}>
                        {canComplete && (
                          <button
                            type="button"
                            onClick={() => handleComplete(row)}
                            disabled={completing[rid]}
                            style={{
                              flex: 1, padding: "11px 16px", borderRadius: 10,
                              border: "none",
                              fontSize: 13, fontWeight: 700,
                              cursor: completing[rid] ? "wait" : "pointer",
                              fontFamily: "inherit",
                              background: completing[rid] 
                                ? "var(--gray-300)" 
                                : (tableId === USER_TABLE ? isUserDone : isDone) 
                                  ? "linear-gradient(135deg, #F59E0B, #D97706)" 
                                  : "linear-gradient(135deg,var(--primary),var(--primary-dark))",
                              color: "white",
                              transition: "all 0.2s ease",
                              boxShadow: completing[rid] 
                                ? "none" 
                                : (tableId === USER_TABLE ? isUserDone : isDone) 
                                  ? "0 4px 12px rgba(245,158,11,0.35)" 
                                  : "0 4px 12px rgba(79,110,247,0.35)",
                              display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                              transform: "translateY(0)",
                              letterSpacing: "0.02em",
                            }}
                            onMouseEnter={e => {
                              if (!completing[rid]) {
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = (tableId === USER_TABLE ? isUserDone : isDone) ? "0 6px 16px rgba(245,158,11,0.5)" : "0 6px 16px rgba(79,110,247,0.5)";
                              }
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = (tableId === USER_TABLE ? isUserDone : isDone) ? "0 4px 12px rgba(245,158,11,0.35)" : "0 4px 12px rgba(79,110,247,0.35)";
                            }}
                            onMouseDown={e => { e.currentTarget.style.transform = "translateY(0) scale(0.98)"; }}
                            onMouseUp={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                          >
                            {completing[rid] ? "⏳ Saving..." : (tableId === USER_TABLE ? isUserDone : isDone) ? "↺ Reopen" : "✓ Mark as Done"}
                          </button>
                        )}
                        <input type="file" multiple style={{ display: "none" }} id={`attach-${rid}`} onChange={(e) => {
                        if (e.target.files?.length && taskId) {
                          Array.from(e.target.files).forEach(f => handleAttachFile(taskId, f));
                          e.target.value = null; // reset
                        }
                      }} />
                      <button type="button" onClick={(e) => { e.preventDefault(); document.getElementById(`attach-${rid}`).click(); }} style={{
                        padding: "9px 12px", borderRadius: 8, border: "none",
                        fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                        background: "#EFF6FF", color: "#1D4ED8",
                      }}>📎</button>
                      <button type="button" onClick={(e) => { e.preventDefault(); setCommentTask(row); }} style={{
                        padding: "9px 12px", borderRadius: 8, border: "none",
                        fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                        background: "#F5F3FF", color: "#7C3AED",
                      }}>💬</button>
                      {canEdit && (
                        <button type="button" onClick={(e) => { e.preventDefault(); setEditRecord(row); }} style={{
                          flex: canComplete ? "0 0 auto" : 1,
                          padding: "9px 12px", borderRadius: 8, border: "none",
                          fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                          background: "var(--primary-light)", color: "var(--primary)",
                        }}>✏️ Edit</button>
                      )}
                      {canDelete && (
                        <button type="button" onClick={(e) => { e.preventDefault(); setDeleteRecord(row); }} style={{
                          padding: "9px 12px", borderRadius: 8, border: "none",
                          fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                          background: "var(--danger-light)", color: "var(--danger)",
                        }}>🗑️</button>
                      )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Modal Comment Panel */}
        {commentTask && (
          <CommentPanel
            taskId={cellText(commentTask.fields?.["Task ID"]) || commentTask.record_id}
            taskName={cellText(commentTask.fields?.["Task Name"]) || "Task"}
            priority={cellText(commentTask.fields?.["Priority"]) || "Medium"}
            onClose={() => setCommentTask(null)}
          />
        )}
      </div>}

      {/* ── GRID VIEW ────────────────────────────────────── */}
      {view === "grid" && (
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1.5px solid var(--gray-200)", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: "inherit" }}>
            <thead>
              <tr>
                {["Task ID","Task Name","Status","Priority","Assigned To","Supervisor","Start Date","Due Date","Company","Department","Recurring","Actions"].map(h => (
                  <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap", position: "sticky", top: 0, background: "var(--primary)", color: "#ffffff", zIndex: 1, borderBottom: "2px solid rgba(255,255,255,0.15)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => {
                const f = row.fields || {};
                const rid = row.record_id || i;
                const statusText = cellText(f["Overall Status"]) || "Not Started";
                const isDone = statusText.toLowerCase().includes("done") || statusText.includes("✅");
                const isUserDone = !!f["My Completion"];
                const priority = cellText(f["Priority"]) || "Medium";
                const isOverdue = statusText.toLowerCase().includes("overdue");
                const effectivelyDone = tableId === USER_TABLE ? isUserDone : isDone;
                const completedAtRaw = f["Completed At"] || f["Completion Time"];
                const completedTime = normalizeToMs(completedAtRaw);
                const dueTime = normalizeToMs(f["End Date"]);
                const isLate = effectivelyDone && completedTime && dueTime && (completedTime > (dueTime + 60000));
                const s = sc(statusText);
                const pb = getPriorityBadge(priority);
                const assignedRaw = f["Total Assigned Users"] || f["All Assigned Users"] || f["Assigned Users"] || f["User"];
                let assignedStr = "—";
                if (Array.isArray(assignedRaw)) {
                  const names = assignedRaw.map(v => v.en_name || v.name || null).filter(Boolean);
                  if (names.length) assignedStr = names.join(", ");
                } else { assignedStr = cellText(assignedRaw) || "—"; }
                const completing = false;
                return (
                  <tr key={rid} style={{
                    background: isLate ? "#FFF5F5" : i % 2 === 0 ? "white" : "#FAFAFA",
                    borderBottom: "1px solid var(--gray-100)",
                    borderLeft: isLate ? "3px solid #EF4444" : isOverdue ? "3px solid #F59E0B" : "3px solid transparent",
                    transition: "background 0.15s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = "#EEF2FF"}
                    onMouseLeave={e => e.currentTarget.style.background = isLate ? "#FFF5F5" : i % 2 === 0 ? "white" : "#FAFAFA"}
                  >
                    <td style={{ padding: "10px 14px", fontWeight: 700, color: "var(--primary)", whiteSpace: "nowrap", fontSize: 12 }}>{cellText(f["Task ID"]) || "—"}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 600, color: "var(--gray-800)", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: effectivelyDone ? "line-through" : "none", color: effectivelyDone ? "var(--gray-400)" : "var(--gray-800)" }}>{cellText(f["Task Name"]) || "Untitled"}</td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      <span style={{ padding: "3px 9px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: isLate ? "#FEE2E2" : s.bg, color: isLate ? "#B91C1C" : s.color }}>
                        {isLate ? "🔴 Overdue (Done)" : statusText}
                      </span>
                    </td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      <span style={{ padding: "3px 9px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: pb.bg, color: pb.color }}>{pb.label}</span>
                    </td>
                    <td style={{ padding: "10px 14px", color: "var(--gray-700)", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{assignedStr}</td>
                    <td style={{ padding: "10px 14px", color: "var(--gray-600)", whiteSpace: "nowrap" }}>{cellText(f["Supervisor"]) || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "var(--gray-500)", whiteSpace: "nowrap", fontSize: 12 }}>{formatDate(f["Start Date"])}</td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap", fontSize: 12, fontWeight: isOverdue ? 700 : 400, color: isOverdue ? "#991B1B" : "var(--gray-500)" }}>{formatDate(f["End Date"])}</td>
                    <td style={{ padding: "10px 14px", color: "var(--gray-500)", whiteSpace: "nowrap", fontSize: 12 }}>{cellText(f["Company Name"]) || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "var(--gray-500)", whiteSpace: "nowrap", fontSize: 12 }}>{cellText(f["Assignee Department"]) || "—"}</td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      {(() => { const rc = cellText(f["Recurring Type"]); return rc && rc !== "—" && rc !== "Not Applicable" ? <span style={{ fontSize: 11, color: "var(--purple)", background: "var(--purple-light)", padding: "2px 8px", borderRadius: 10 }}>🔄 {rc}</span> : <span style={{ color: "var(--gray-300)", fontSize: 12 }}>—</span>; })()}
                    </td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 5 }}>
                        <button onClick={() => setCommentTask(row)} style={{ padding: "5px 9px", borderRadius: 7, border: "none", background: "#F5F3FF", color: "#7C3AED", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>💬</button>
                        {canComplete && (
                          <button onClick={() => handleComplete(row)} style={{ padding: "5px 9px", borderRadius: 7, border: "none", background: effectivelyDone ? "#FEF3C7" : "var(--primary-light)", color: effectivelyDone ? "#D97706" : "var(--primary)", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }} title={effectivelyDone ? "Reopen Task" : "Mark as Done"}>{effectivelyDone ? "↺" : "✓"}</button>
                        )}
                        {canEdit && (
                          <button onClick={() => setEditRecord(row)} style={{ padding: "5px 9px", borderRadius: 7, border: "none", background: "var(--primary-light)", color: "var(--primary)", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>✏️</button>
                        )}
                        {canDelete && (
                          <button onClick={() => setDeleteRecord(row)} style={{ padding: "5px 9px", borderRadius: 7, border: "none", background: "var(--danger-light)", color: "var(--danger)", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {commentTask && (
            <CommentPanel
              taskId={cellText(commentTask.fields?.["Task ID"]) || commentTask.record_id}
              taskName={cellText(commentTask.fields?.["Task Name"]) || "Task"}
              priority={cellText(commentTask.fields?.["Priority"]) || "Medium"}
              onClose={() => setCommentTask(null)}
            />
          )}
        </div>
      )}

      {/* ── GANTT VIEW ────────────────────────────────────── */}
      {view === "gantt" && (() => {
        const ROW_H = 44;
        const LEFT_W = 220;
        const DAY_W = 36;
        const totalDays = Math.ceil(ganttRange.totalMs / 86400000);
        const days = Array.from({ length: totalDays }, (_, i) => new Date(ganttRange.start + i * 86400000));
        const today = Date.now();
        const todayOffset = ((today - ganttRange.start) / ganttRange.totalMs) * 100;
        const barColor = (st, late) => {
          if (late) return "#EF4444";
          const s = (st || "").toLowerCase();
          if (s.includes("overdue")) return "#F97316";
          if (s.includes("done") || s.includes("complet")) return "#10B981";
          if (s.includes("progress")) return "#F59E0B";
          return "var(--primary)";
        };
        return (
          <div style={{ borderRadius: 12, border: "1.5px solid var(--gray-200)", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
            <div style={{ display: "flex" }}>
              {/* Left panel — task list */}
              <div style={{ width: LEFT_W, minWidth: LEFT_W, flexShrink: 0, borderRight: "2px solid var(--gray-200)", zIndex: 2 }}>
                <div style={{ height: ROW_H, background: "var(--primary)", display: "flex", alignItems: "center", padding: "0 14px", fontWeight: 700, fontSize: 12, color: "white", letterSpacing: "0.04em" }}>TASK</div>
                {filtered.map((row, i) => {
                  const f = row.fields || {};
                  const rid = row.record_id || i;
                  const statusText = cellText(f["Overall Status"]) || "Not Started";
                  const effectivelyDone = tableId === USER_TABLE ? !!f["My Completion"] : (statusText.toLowerCase().includes("done") || statusText.includes("✅"));
                  return (
                    <div key={rid} style={{
                      height: ROW_H, display: "flex", flexDirection: "column", justifyContent: "center",
                      padding: "0 14px", borderBottom: "1px solid var(--gray-100)",
                      background: i % 2 === 0 ? "white" : "#FAFAFA",
                    }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--primary)", lineHeight: 1 }}>{cellText(f["Task ID"]) || `#${i + 1}`}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: effectivelyDone ? "var(--gray-400)" : "var(--gray-800)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: effectivelyDone ? "line-through" : "none", lineHeight: 1.3, marginTop: 2 }}>
                        {cellText(f["Task Name"]) || "Untitled"}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right panel — timeline */}
              <div style={{ flex: 1, overflowX: "auto", position: "relative" }}>
                {/* Day header */}
                <div style={{ display: "flex", height: ROW_H, background: "var(--primary)", position: "sticky", top: 0, zIndex: 1 }}>
                  {days.map((d, idx) => {
                    const isToday = d.toDateString() === new Date().toDateString();
                    const showMonth = idx === 0 || d.getDate() === 1;
                    return (
                      <div key={idx} style={{
                        minWidth: DAY_W, width: DAY_W, textAlign: "center", fontSize: 10, color: isToday ? "#FCD34D" : "rgba(255,255,255,0.75)",
                        fontWeight: isToday ? 800 : showMonth ? 700 : 400, display: "flex", flexDirection: "column",
                        alignItems: "center", justifyContent: "center", borderRight: "1px solid rgba(255,255,255,0.1)",
                        background: isToday ? "rgba(255,255,255,0.12)" : "transparent",
                      }}>
                        {showMonth && <span style={{ fontSize: 8, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.9 }}>{d.toLocaleDateString("en", { month: "short" })}</span>}
                        <span>{d.getDate()}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Task bars */}
                {filtered.map((row, i) => {
                  const f = row.fields || {};
                  const rid = row.record_id || i;
                  const statusText = cellText(f["Overall Status"]) || "Not Started";
                  const effectivelyDone = tableId === USER_TABLE ? !!f["My Completion"] : (statusText.toLowerCase().includes("done") || statusText.includes("✅"));
                  const completedAtRaw = f["Completed At"] || f["Completion Time"];
                  const completedTime = normalizeToMs(completedAtRaw);
                  const dueTime = normalizeToMs(f["End Date"]);
                  const isLate = effectivelyDone && completedTime && dueTime && (completedTime > (dueTime + 60000));
                  let startMs = normalizeToMs(f["Start Date"]);
                  let endMs = normalizeToMs(f["End Date"]);
                  if (!startMs || startMs <= 0) startMs = today;
                  if (!endMs || endMs <= 0) endMs = startMs + 86400000;
                  if (endMs < startMs) endMs = startMs + 86400000;
                  const leftPct = Math.max(0, ((startMs - ganttRange.start) / ganttRange.totalMs) * 100);
                  const widthPct = Math.max(0.5, ((endMs - startMs) / ganttRange.totalMs) * 100);
                  const color = barColor(statusText, isLate);
                  const taskLabel = cellText(f["Task Name"]) || "Untitled";
                  return (
                    <div key={rid} style={{
                      height: ROW_H, position: "relative", borderBottom: "1px solid var(--gray-100)",
                      background: i % 2 === 0 ? "white" : "#FAFAFA",
                      display: "flex", alignItems: "center",
                      minWidth: totalDays * DAY_W,
                    }}>
                      {/* Day grid lines */}
                      {days.map((d, di) => (
                        <div key={di} style={{ position: "absolute", left: di * DAY_W, top: 0, bottom: 0, width: 1, background: d.toDateString() === new Date().toDateString() ? "rgba(239,68,68,0.3)" : "var(--gray-100)" }} />
                      ))}
                      {/* Task bar */}
                      <div style={{
                        position: "absolute",
                        left: `calc(${leftPct}% + 3px)`,
                        width: `calc(${widthPct}% - 6px)`,
                        height: 26, borderRadius: 6,
                        background: color,
                        opacity: effectivelyDone ? 0.7 : 1,
                        display: "flex", alignItems: "center", paddingLeft: 8, overflow: "hidden",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                        cursor: "default",
                        transition: "opacity 0.15s",
                      }} title={`${taskLabel}\n${formatDate(f["Start Date"])} → ${formatDate(f["End Date"])}`}>
                        <span style={{ color: "white", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{taskLabel}</span>
                      </div>
                      {/* Today line */}
                      {todayOffset >= 0 && todayOffset <= 100 && (
                        <div style={{ position: "absolute", left: `${todayOffset}%`, top: 0, bottom: 0, width: 2, background: "#EF4444", opacity: 0.8, zIndex: 2, pointerEvents: "none" }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {commentTask && (
              <CommentPanel
                taskId={cellText(commentTask.fields?.["Task ID"]) || commentTask.record_id}
                taskName={cellText(commentTask.fields?.["Task Name"]) || "Task"}
                priority={cellText(commentTask.fields?.["Priority"]) || "Medium"}
                onClose={() => setCommentTask(null)}
              />
            )}
          </div>
        );
      })()}

      {editRecord && <EditTaskModal record={editRecord} tableId={tableId} onClose={() => setEditRecord(null)} />}
      {deleteRecord && <DeleteConfirmModal record={deleteRecord} tableId={tableId} onClose={() => setDeleteRecord(null)} />}
    </div>
  );
}
